import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import OrderTypeBadge, { ORDER_TYPE_COLORS } from "@/components/OrderTypeBadge";
import { Save, Plus, Trash2 } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const canEdit = user.role === "admin";
  const [maxDiscount, setMaxDiscount] = useState("0");
  const [b2bAutoApprove, setB2bAutoApprove] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAutoApprove, setSavingAutoApprove] = useState(false);

  const [platforms, setPlatforms] = useState([]);
  const [newPlatform, setNewPlatform] = useState("");
  const [addingPlatform, setAddingPlatform] = useState(false);

  const [orderTypes, setOrderTypes] = useState([]);
  const [newOrderType, setNewOrderType] = useState({ name: "", color: "gray" });
  const [addingOrderType, setAddingOrderType] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setMaxDiscount(String(data?.max_discount_percentage ?? 0));
      setB2bAutoApprove(Boolean(data?.b2b_auto_approve ?? true));
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadPlatforms = useCallback(async () => {
    try {
      const { data } = await api.get("/platforms");
      setPlatforms(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  const loadOrderTypes = useCallback(async () => {
    try {
      const { data } = await api.get("/order-types");
      setOrderTypes(Array.isArray(data) ? data : []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); loadPlatforms(); loadOrderTypes(); }, [load, loadPlatforms, loadOrderTypes]);

  const save = async () => {
    const pct = Number(maxDiscount);
    if (!Number.isFinite(pct) || pct < 0 || pct > 100) {
      return toast.error("Enter a value between 0 and 100");
    }
    setSaving(true);
    try {
      await api.put("/settings", { max_discount_percentage: pct });
      toast.success("Settings saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const toggleB2bAutoApprove = async (checked) => {
    const prev = b2bAutoApprove;
    setB2bAutoApprove(checked);
    setSavingAutoApprove(true);
    try {
      await api.put("/settings", { b2b_auto_approve: checked });
      toast.success(checked
        ? "Auto approval ON — new B2B bills are approved automatically"
        : "Auto approval OFF — every B2B bill needs your manual approval");
    } catch (e) {
      setB2bAutoApprove(prev);
      toast.error(formatApiError(e));
    } finally { setSavingAutoApprove(false); }
  };

  const addPlatform = async () => {
    const name = newPlatform.trim();
    if (!name) return toast.error("Enter a platform name");
    setAddingPlatform(true);
    try {
      await api.post("/platforms", { name });
      toast.success("Platform added");
      setNewPlatform("");
      loadPlatforms();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddingPlatform(false); }
  };

  const deletePlatform = async (id) => {
    if (!window.confirm("Delete this platform?")) return;
    try {
      await api.delete(`/platforms/${id}`);
      toast.success("Platform deleted");
      loadPlatforms();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addOrderType = async () => {
    const name = newOrderType.name.trim();
    if (!name) return toast.error("Enter an order type name");
    if (!ORDER_TYPE_COLORS.includes(newOrderType.color)) return toast.error("Pick a color");
    setAddingOrderType(true);
    try {
      await api.post("/order-types", { name, color: newOrderType.color });
      toast.success("Order type added");
      setNewOrderType({ name: "", color: "gray" });
      loadOrderTypes();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAddingOrderType(false); }
  };

  const deleteOrderType = async (id) => {
    if (!window.confirm("Delete this order type? Existing orders keep their label.")) return;
    try {
      await api.delete(`/order-types/${id}`);
      toast.success("Order type deleted");
      loadOrderTypes();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight">Settings</h1>
        <p className="text-sm text-muted-foreground">Global controls that apply across the system</p>
      </div>

      <Card className="p-4 md:p-6 space-y-4">
        <div>
          <h2 className="font-display font-semibold text-lg">Order discount</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Maximum discount percentage a sales BDM can apply on any single order. Set to <span className="font-mono">0</span> to disable discounts entirely.
          </p>
        </div>
        <div className="space-y-2">
          <Label>Maximum discount (%)</Label>
          <Input
            type="number" step="0.01" min="0" max="100"
            value={maxDiscount}
            disabled={!canEdit}
            onChange={(e) => setMaxDiscount(e.target.value)}
            data-testid="max-discount-input"
          />
        </div>
        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
              <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save"}
            </Button>
          </div>
        )}
        {!canEdit && (
          <div className="text-xs text-muted-foreground">Read-only — only admins can change this.</div>
        )}
      </Card>

      <Card className="p-4 md:p-6 space-y-4" data-testid="b2b-approval-card">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="font-display font-semibold text-lg">B2B bill approval</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {b2bAutoApprove
                ? "Auto approval is ON — B2B bills submitted by IBOs are approved automatically."
                : "Auto approval is OFF — every B2B bill waits in Pending Approval until you approve it manually."}
            </p>
          </div>
          <Switch
            checked={b2bAutoApprove}
            onCheckedChange={toggleB2bAutoApprove}
            disabled={!canEdit || savingAutoApprove}
            data-testid="b2b-auto-approve-toggle"
          />
        </div>
        {!canEdit && (
          <div className="text-xs text-muted-foreground">Read-only — only admins can change this.</div>
        )}
      </Card>

      <Card className="p-4 md:p-6 space-y-4" data-testid="platforms-card">
        <div>
          <h2 className="font-display font-semibold text-lg">Claim platforms</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Platforms available for BDMs to select when filing a claim (e.g. Website, Amazon, Flipkart, Blinkit).
          </p>
        </div>

        {canEdit && (
          <div className="flex gap-2">
            <Input
              placeholder="New platform name"
              value={newPlatform}
              onChange={(e) => setNewPlatform(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addPlatform(); }}
              data-testid="add-platform-input"
            />
            <Button onClick={addPlatform} disabled={addingPlatform} data-testid="add-platform-btn">
              <Plus className="w-4 h-4 mr-1" /> {addingPlatform ? "Adding..." : "Add"}
            </Button>
          </div>
        )}

        <div className="divide-y border rounded-md">
          {platforms.length === 0 && (
            <div className="p-3 text-sm text-muted-foreground" data-testid="platforms-empty">
              No platforms added yet.
            </div>
          )}
          {platforms.map((p) => (
            <div
              key={p.id}
              className="flex items-center justify-between p-3"
              data-testid={`platform-row-${p.id}`}
            >
              <span className="text-sm font-medium">{p.name}</span>
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deletePlatform(p.id)}
                  data-testid={`delete-platform-${p.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>

      <Card className="p-4 md:p-6 space-y-4" data-testid="order-types-card">
        <div>
          <h2 className="font-display font-semibold text-lg">Order types</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Custom labels a BDM picks on every order/claim (e.g. Repeat, VIP, Bulk). Each has a color used across tables and exports.
          </p>
        </div>

        {canEdit && (
          <div className="space-y-2">
            <div className="flex gap-2">
              <Input
                placeholder="New order type name"
                value={newOrderType.name}
                onChange={(e) => setNewOrderType({ ...newOrderType, name: e.target.value })}
                onKeyDown={(e) => { if (e.key === "Enter") addOrderType(); }}
                data-testid="add-order-type-input"
              />
              <Button onClick={addOrderType} disabled={addingOrderType} data-testid="add-order-type-btn">
                <Plus className="w-4 h-4 mr-1" /> {addingOrderType ? "Adding..." : "Add"}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {ORDER_TYPE_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setNewOrderType({ ...newOrderType, color: c })}
                  className={`px-2 py-1 rounded text-xs font-medium border transition-all ${newOrderType.color === c ? "ring-2 ring-primary" : ""}`}
                  data-testid={`order-type-color-${c}`}
                >
                  <OrderTypeBadge name={c} color={c} />
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="divide-y border rounded-md">
          {orderTypes.length === 0 && (
            <div className="p-3 text-sm text-muted-foreground" data-testid="order-types-empty">
              No order types added yet.
            </div>
          )}
          {orderTypes.map((t) => (
            <div
              key={t.id}
              className="flex items-center justify-between p-3"
              data-testid={`order-type-row-${t.id}`}
            >
              <OrderTypeBadge name={t.name} color={t.color} />
              {canEdit && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => deleteOrderType(t.id)}
                  data-testid={`delete-order-type-${t.id}`}
                >
                  <Trash2 className="w-4 h-4 text-red-500" />
                </Button>
              )}
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
